/** The editor's left sidebar (page/section/block tree, Style, Options, Library). */
export const PRESENTATION_EDIT_SIDEBAR_ID = 'presentation-edit-sidebar'

/** The editor's right inspector (block/media/logo options) — always mounted while editable. */
export const PRESENTATION_INSPECTOR_ID = 'presentation-inspector'

/**
 * Whether a click landed on the editor's own chrome — the left sidebar or right inspector — rather
 * than on the canvas.
 *
 * Used by the canvas's click-away handling to *not* clear the focused block when the click came from
 * the sidebar tree (selecting a row there sets the selection itself: focus the block it scrolled to,
 * clear it for a page/section row) or from the inspector (clicking a field inside it must not deselect
 * the block whose options it's editing). Both sidebar and canvas click-away used to fire independently
 * and whichever ran last won, so picking a block row while another block was focused could end up with
 * nothing focused.
 *
 * Note the top bar is deliberately NOT chrome here: clicking Save/undo should still blur and commit
 * the focused block, which is what the canvas click-away does.
 */
export function isEditorChromeClick(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) {
    return false
  }

  if (target.closest(`#${PRESENTATION_EDIT_SIDEBAR_ID}`) || target.closest(`#${PRESENTATION_INSPECTOR_ID}`)) {
    return true
  }

  // A **detached** target can't be proven to be canvas, so don't treat it as one.
  //
  // This is the common case, not an edge case: selecting a tree row re-renders the tree, so by the
  // time the document-level click-away handlers run, the row the user clicked has been removed from
  // the DOM and its ancestor chain no longer reaches the sidebar — `closest` returns null and the
  // click reads as a canvas click. (MUI's own ClickAwayListener bails out on the same signal, via
  // `!doc.documentElement.contains(event.target)`.)
  return !target.ownerDocument?.documentElement?.contains(target)
}

/**
 * Shared by InlineEditableMedia/InlineEditableLogo's own click-away handlers: drops the inspector's
 * drill-in (`focusedElement`) unless the click landed on the inspector itself (editing this very
 * element's options must not blur it) or `skip` says not to (media's `getFocusFromSection` case,
 * where an auto-selected element shouldn't lose focus to its own initial click-away pass).
 */
export function clearDrillInOnClickAway(args: {
  target: EventTarget | null
  isDrilledIn: boolean
  setFocusedElement: (elementKey: string) => void
  skip?: boolean
}): void {
  const { target, isDrilledIn, setFocusedElement, skip } = args

  if (skip || isEditorChromeClick(target)) {
    return
  }

  if (isDrilledIn) {
    setFocusedElement('')
  }
}
